<template>
    <App title="Make A Claim">
        <div class="claim-layout">

            <!-- ── Ticket shell ─────────────────────────────────────────── -->
            <div class="claim-shell">

                <!-- Top bar -->
                <div class="claim-topbar">
                    <div class="claim-topbar-left">
                        <div class="claim-topbar-icon">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 5v2M15 11v2M15 17v2M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z"/></svg>
                        </div>
                        <div>
                            <p class="claim-topbar-eyebrow">Claim Request</p>
                            <h1 class="claim-topbar-title">Make A Claim</h1>
                        </div>
                    </div>
                    <div class="claim-topbar-status">
                        <span v-if="!response"  class="claim-badge claim-badge--waiting">Waiting</span>
                        <span v-else-if="done"  class="claim-badge claim-badge--done">Claimed</span>
                        <span v-else            class="claim-badge claim-badge--open">Open</span>
                    </div>
                </div>

                <!-- Alert bar -->
                <div v-if="message" class="claim-alert" :class="message === 'success' ? 'claim-alert--success' : 'claim-alert--warning'">
                    <i class="fas fa-info-circle me-2"></i>{{ message }}
                </div>

                <!-- Body -->
                <div class="claim-body">

                    <!-- Sidebar -->
                    <aside class="claim-sidebar">
                        <div class="claim-sidebar-row">
                            <p class="claim-meta-label">Ticket Type</p>
                            <p class="claim-meta-value">Claim Request</p>
                        </div>
                        <div class="claim-sidebar-row">
                            <p class="claim-meta-label">Status</p>
                            <span v-if="!response"  class="claim-badge claim-badge--waiting">Awaiting Lookup</span>
                            <span v-else-if="done"  class="claim-badge claim-badge--done">Code Claimed</span>
                            <span v-else            class="claim-badge claim-badge--open">Record Found</span>
                        </div>
                        <template v-if="response">
                            <div class="claim-sidebar-row">
                                <p class="claim-meta-label">Order Code</p>
                                <p class="claim-meta-value font-monospace">{{ response.value }}</p>
                            </div>
                            <div class="claim-sidebar-row">
                                <p class="claim-meta-label">Station</p>
                                <p class="claim-meta-value">{{ response.rider?.station_name }}</p>
                            </div>
                            <div class="claim-sidebar-row">
                                <p class="claim-meta-label">Remaining Claims</p>
                                <span :class="['claim-badge', response.claims_remaining === 0 ? 'claim-badge--danger' : 'claim-badge--open']">
                                    {{ response.claims_remaining }} left
                                </span>
                            </div>
                            <div class="claim-sidebar-row">
                                <p class="claim-meta-label">Engine Status</p>
                                <span :class="['claim-badge', response?.order?.engine?.activated_at ? 'claim-badge--done' : 'claim-badge--danger']">
                                    {{ response?.order?.engine?.activated_at ? 'Activated' : 'Inactive' }}
                                </span>
                            </div>
                        </template>
                    </aside>

                    <!-- Main content -->
                    <main class="claim-main">

                        <!-- Lookup panel -->
                        <div class="claim-panel">
                            <div class="claim-panel-header">
                                <i class="fas fa-search me-2"></i> Lookup Order
                            </div>
                            <div class="claim-panel-body">
                                <Form ref="form" @submit="onSubmit">
                                    <div class="row g-3">
                                        <div class="col-12 col-md-6">
                                            <InputField id="code" v-model="code" name="code" label="Order Code" rules="required" placeholder="e.g. ORD-00123" :disabled="loading" />
                                        </div>
                                        <div class="col-12 col-md-6">
                                            <SelectField id="station" v-model="station" name="station" rules="required" label="Select Station" :options="type_options" :disabled = "!isSuperAdmin" />
                                        </div>
                                    </div>
                                    <div class="claim-lookup-actions">
                                        <button type="button" class="btn btn-outline-danger btn-sm" @click="onReset">
                                            <i class="fas fa-undo me-1"></i> Reset
                                        </button>
                                        <Button class="btn btn-sm claim-check-btn" :label="label" type="button" @click="onCheck" :disabled="checkInput || loading" />
                                    </div>
                                </Form>
                            </div>
                        </div>

                        <!-- Found banner -->
                        <div v-if="response" class="claim-found-banner">
                            <span class="claim-found-dot">
                                <i class="fas fa-check"></i>
                            </span>
                            <div>
                                <p class="claim-found-eyebrow">Lookup Complete</p>
                                <p class="claim-found-text">Order found — review the details below</p>
                            </div>
                        </div>

                        <!-- Order details -->
                        <div v-if="response" class="claim-panel">
                            <div class="claim-panel-header">
                                <i class="fas fa-file-invoice me-2"></i> Order Details
                            </div>
                            <div class="claim-panel-body">
                                <div class="claim-detail-grid">
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Quantity</span>
                                        <span class="claim-meta-value">{{ response?.order?.quantity }}</span>
                                    </div>
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Amount Paid</span>
                                        <span class="claim-meta-value fw-bold">{{ response?.order?.amount?.formatted }}</span>
                                    </div>
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Currency</span>
                                        <span class="claim-meta-value">{{ response?.order?.amount?.currency }}</span>
                                    </div>
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Paid by</span>
                                        <span class="claim-meta-value">{{ response.rider }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Engine details -->
                        <div v-if="response" class="claim-panel">
                            <div class="claim-panel-header">
                                <i class="fas fa-cog me-2"></i> Engine Details
                            </div>
                            <div class="claim-panel-body">
                                <div class="claim-detail-grid">
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Engine No.</span>
                                        <span class="claim-meta-value font-monospace">{{ response?.order?.engine?.serial }}</span>
                                    </div>
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Activation Status</span>
                                        <span :class="['claim-badge', response?.order?.engine?.activated_at ? 'claim-badge--done' : 'claim-badge--danger']">
                                            {{ response?.order?.engine?.activated_at ? 'Activated' : 'Inactive' }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Agent info -->
                        <div v-if="response" class="claim-panel">
                            <div class="claim-panel-header">
                                <i class="fas fa-user me-2"></i> Agent Info
                            </div>
                            <div class="claim-panel-body">
                                <div class="claim-detail-grid">
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">First Name</span>
                                        <span class="claim-meta-value">{{ response?.order?.agent?.firstname }}</span>
                                    </div>
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Last Name</span>
                                        <span class="claim-meta-value">{{ response?.order?.agent?.lastname }}</span>
                                    </div>
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Phone</span>
                                        <span class="claim-meta-value">{{ response?.order?.agent?.phone }}</span>
                                    </div>
                                    <div class="claim-detail-item">
                                        <span class="claim-meta-label">Email</span>
                                        <span class="claim-meta-value">{{ response?.order?.agent?.email }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Submit footer -->
                        <div v-if="response" class="claim-submit-bar">
                            <p v-if="response.claims_remaining === 0" class="claim-no-claims">
                                <i class="fas fa-exclamation-circle me-1"></i> No remaining claims on this order.
                            </p>
                            <div class="ms-auto">
                                <button
                                    @click="onSubmit"
                                    :disabled="loading || done || response.claims_remaining === 0"
                                    class="btn claim-submit-btn"
                                >
                                    <i v-if="done" class="fas fa-check me-2"></i>
                                    <i v-else class="fas fa-paper-plane me-2"></i>
                                    {{ done ? 'Claim Submitted' : 'Submit Claim' }}
                                </button>
                            </div>
                        </div>

                    </main>
                </div>
            </div>

        </div>
    </App>
</template>

<script lang="ts">
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import TextBlock from '@/vue/components/TextBlock.vue'
import InputField from '@/vue/components/InputField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import Form from '@/vue/components/Form.vue'
import App from '@/vue/templates/App.vue'
import Button from '@/vue/components/Button.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import { AxiosError } from 'axios'
import { Component, Vue, toNative } from 'vue-facing-decorator'
import { Notivue, Notification, push, materialTheme, NotificationProgress } from 'notivue'
import $ from 'jquery'
import Swal from 'sweetalert2'

@Component({
    components: {
        App, Block, Alert, TextBlock, InputField, Button, SelectField, Form,
        Notivue, Notification, NotificationProgress
    }
})
class AppLayout extends Vue {
    message = ''
    loading = false
    done = false
    code = ''
    station = ''
    materialTheme = materialTheme
    response = null as any

    get label(): ButtonLabel {
        return { normal: 'Check', loading: 'Checking...', done: 'Completed' }
    }

    get isSuperAdmin(){
        return this.$user?.role === "super_admin"
    }
    get type_options(): Select2Options {
		return {
			placeholder: 'Select Station',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('station.ajax.select.station'),
			},
            data: !this.isSuperAdmin ? [{
			id: this.$user?.workplace?.slug,
			text: `${this.$user?.workplace?.title}`,
			title: `${this.$user?.workplace?.title}`,
			selected: true,
			}]:[],
			//@ts-ignore
			templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
		}
	}

    onReset() {
        this.code = ''; this.station = ''; this.response = null; this.done = false; this.message = ''
    }

    get checkInput(): boolean {
        return [this.code, this.station].some(f => f.trim() === '')
    }

    onCheck() {
        this.loading = true
        this.$http.get(this.route('code.ajax.check', { identifier: this.code }))
            .then(({ data }) => {
                this.response = data.data; this.done = false; this.message = data.message
                setTimeout(() => { this.loading = false; this.message = '' }, 2000)
            })
            .catch((e: AxiosError) => {
                this.response = null; this.done = false
                this.message = e.response?.data?.message || 'An error occurred while checking the code.'
            })
            .finally(() => this.loading = false)
    }

    mounted() {
        if (!this.isSuperAdmin) {
            this.station = this.$user.workplace.slug
        }
    }


	showToast(message: string, type: 'success' | 'error' ) {
			Swal.fire({
				toast: true,
				position: 'top-end', 
				icon: type, 
				title: message,
				showConfirmButton: false, 
				timer: 5000, 
				timerProgressBar: true,
				didOpen: (toast) => {
					// Pause the timer when the mouse hovers over the toast
					toast.addEventListener('mouseenter', Swal.stopTimer)
					// Resume the timer when the mouse leaves
					toast.addEventListener('mouseleave', Swal.resumeTimer)
				}
			})
		}

    onSubmit() {
        this.loading = true
        this.$http.post(this.route('claim.ajax.create'), { identifier: this.code, station: this.station })
            .then(({ data }) => {
                this.message = data.message; this.done = true
                this.showToast(this.message || 'Your claim has been successfully processed.', 'success')
                setTimeout(() => { this.router.visit(this.route('claim.page.create')) }, 2000)
            })
            .catch((e: AxiosError) => {
                this.response = null; this.done = false
                this.message = e.response?.data?.message || 'An error occurred while making a claim. Please try again!'
                this.showToast(this.message, 'error')
                console.error(e)
            })
            .finally(() => this.loading = false)
    }
}
export default toNative(AppLayout)
</script>

<style scoped lang="scss">
// ─── Layout ────────────────────────────────────────────────────────────────
.claim-layout {
    max-width: 1000px;
    margin: 0 auto;
}

.claim-shell {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

// ─── Top bar ───────────────────────────────────────────────────────────────
.claim-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;
    background: #fff;
}

.claim-topbar-left {
    display: flex;
    align-items: center;
    gap: 0.875rem;
}

.claim-topbar-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.claim-topbar-eyebrow {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #9ca3af;
    margin-bottom: 0.1rem;
}

.claim-topbar-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
}

// ─── Alert bar ─────────────────────────────────────────────────────────────
.claim-alert {
    padding: 0.6rem 1.5rem;
    font-size: 0.82rem;
    font-weight: 500;
    border-bottom: 1px solid;

    &--success {
        background: #f0fdf4;
        color: #15803d;
        border-color: #bbf7d0;
    }

    &--warning {
        background: #fffbeb;
        color: #b45309;
        border-color: #fde68a;
    }
}

// ─── Body grid ─────────────────────────────────────────────────────────────
.claim-body {
    display: grid;
    grid-template-columns: 210px 1fr;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

// ─── Sidebar ───────────────────────────────────────────────────────────────
.claim-sidebar {
    border-right: 1px solid #e9ecef;
    background: #f9fafb;
    padding: 0;
}

.claim-sidebar-row {
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid #f1f3f5;
}

// ─── Shared meta typography ────────────────────────────────────────────────
.claim-meta-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #9ca3af;
    margin-bottom: 0.3rem;
}

.claim-meta-value {
    font-size: 0.85rem;
    font-weight: 500;
    color: #111827;
    margin: 0;
    word-break: break-word;
}

// ─── Badges ────────────────────────────────────────────────────────────────
.claim-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;

    &--waiting {
        background: #f3f4f6;
        color: #6b7280;
        border: 1px solid #e5e7eb;
    }

    &--open {
        background: #dbeafe;
        color: #1d4ed8;
        border: 1px solid #bfdbfe;
    }

    &--done {
        background: #dcfce7;
        color: #15803d;
        border: 1px solid #bbf7d0;
    }

    &--danger {
        background: #fee2e2;
        color: #dc2626;
        border: 1px solid #fecaca;
    }
}

// ─── Main area ─────────────────────────────────────────────────────────────
.claim-main {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.claim-panel {
    border-bottom: 1px solid #f1f3f5;

    &:last-child {
        border-bottom: none;
    }
}

.claim-panel-header {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    background: #f9fafb;
    border-bottom: 1px solid #f1f3f5;
}

.claim-panel-body {
    padding: 1.25rem 1.5rem;
}

// ─── Lookup actions ────────────────────────────────────────────────────────
.claim-lookup-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1rem;
}

.claim-check-btn {
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    color: #fff;
    border: none;

    &:hover:not(:disabled) {
        background: linear-gradient(135deg, #152b22, #2d6140);
        color: #fff;
    }

    &:disabled {
        opacity: 0.55;
    }
}

// ─── Found banner ──────────────────────────────────────────────────────────
.claim-found-banner {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 0.875rem 1.5rem;
    background: #f0fdf4;
    border-bottom: 1px solid #bbf7d0;
}

.claim-found-dot {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    flex-shrink: 0;
}

.claim-found-eyebrow {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #15803d;
    margin-bottom: 0.1rem;
}

.claim-found-text {
    font-size: 0.82rem;
    font-weight: 500;
    color: #374151;
    margin: 0;
}

// ─── Detail grid ───────────────────────────────────────────────────────────
.claim-detail-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.claim-detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

// ─── Submit bar ────────────────────────────────────────────────────────────
.claim-submit-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    background: #f9fafb;
    border-top: 1px solid #f1f3f5;
}

.claim-no-claims {
    font-size: 0.82rem;
    font-weight: 500;
    color: #dc2626;
    margin: 0;
}

.claim-submit-btn {
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    color: #fff;
    border: none;
    padding: 0.5rem 1.5rem;
    font-weight: 600;

    &:hover:not(:disabled) {
        background: linear-gradient(135deg, #152b22, #2d6140);
        color: #fff;
    }

    &:disabled {
        opacity: 0.55;
    }
}
</style>
